import React from 'react';
import { Modal, Button, Tag } from 'antd';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Box, Flex, Text } from 'atoms';

const StyledModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 8px;
	}

	.ant-modal-header {
		border-bottom: none;
		padding: 24px 24px 16px;
	}

	.ant-modal-title {
		font-size: 18px;
		font-weight: 600;
		color: #000;
	}

	.ant-modal-body {
		padding: 0 24px 24px;
	}

	.ant-modal-footer {
		border-top: none;
		padding: 0 24px 24px;
		display: flex;
		justify-content: flex-end;
		gap: 12px;
	}
`;

const Description = styled(Text)`
	font-size: 14px;
	color: #666;
	display: block;
	margin-bottom: 16px;
`;

const TripIdBox = styled(Box)`
	max-height: 200px;
	overflow-y: auto;
	border: 1px solid #e8e8e8;
	padding: 12px;
	margin-bottom: 16px;
	font-size: 13px;
	color: #333;
	line-height: 1.6;
	word-break: break-word;
`;

const CancelButton = styled(Button)`
	height: 40px;
	padding: 0 24px;
	border: 1px solid #d9d9d9;
	color: #000;
	font-weight: 500;

	&:hover {
		border-color: #18405d !important;
		color: #18405d !important;
	}
`;

const ConfirmButton = styled(Button)`
	height: 40px;
	padding: 0 24px;
	background: #18405d;
	border-color: #18405d;
	color: #fff;
	font-weight: 500;

	&:hover {
		background: #1e4a6b !important;
		border-color: #1e4a6b !important;
	}
`;

const ResultRow = styled(Flex)`
	align-items: baseline;
	gap: 8px;
	margin-bottom: 8px;
`;

/**
 * Confirm-then-close-then-show-result modal for bulk trip closure.
 * `result` (once set by the caller after the bulk-close call resolves) swaps the
 * body into a success/failure breakdown instead of the confirmation view.
 */
export default function BulkCloseTripsModal({
	visible,
	selectedTrips,
	loading,
	result,
	onCancel,
	onConfirm,
	onDone,
}) {
	const tripIds = selectedTrips.map((t) => t.id);
	const showResult = Boolean(result);

	return (
		<StyledModal
			open={visible}
			onCancel={showResult ? onDone : onCancel}
			title={showResult ? 'Bulk Close Result' : 'Close Selected Trips'}
			footer={
				showResult ? (
					<ConfirmButton onClick={onDone}>Done</ConfirmButton>
				) : (
					[
						<CancelButton key="cancel" onClick={onCancel}>
							Cancel
						</CancelButton>,
						<ConfirmButton key="confirm" loading={loading} onClick={onConfirm}>
							Close {tripIds.length} Trip{tripIds.length === 1 ? '' : 's'}
						</ConfirmButton>,
					]
				)
			}
		>
			{showResult ? (
				<>
					<Description>
						{result.success_count} of {result.total_count} trip(s) closed.
					</Description>
					{result.closed?.length > 0 && (
						<Box mb="16px">
							<Text fontWeight={600} display="block" mb="8px">
								Closed
							</Text>
							{result.closed.map((t) => (
								<ResultRow key={t.trip_id}>
									<Tag color="green">Trip {t.trip_id}</Tag>
								</ResultRow>
							))}
						</Box>
					)}
					{result.failed?.length > 0 && (
						<Box>
							<Text fontWeight={600} display="block" mb="8px">
								Not Closed
							</Text>
							{result.failed.map((t) => (
								<ResultRow key={t.trip_id}>
									<Tag color="red">Trip {t.trip_id}</Tag>
									<Text fontSize="13px" color="#666">
										{t.reason}
									</Text>
								</ResultRow>
							))}
						</Box>
					)}
				</>
			) : (
				<>
					<Description>
						You are about to close {tripIds.length} trip
						{tripIds.length === 1 ? '' : 's'}. This action cannot be undone.
					</Description>
					<TripIdBox>{tripIds.join(', ')}</TripIdBox>
				</>
			)}
		</StyledModal>
	);
}

BulkCloseTripsModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	selectedTrips: PropTypes.arrayOf(
		PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) })
	).isRequired,
	loading: PropTypes.bool,
	result: PropTypes.shape({
		total_count: PropTypes.number,
		success_count: PropTypes.number,
		closed: PropTypes.array,
		failed: PropTypes.array,
	}),
	onCancel: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	onDone: PropTypes.func.isRequired,
};

BulkCloseTripsModal.defaultProps = {
	loading: false,
	result: null,
};
